// passkey-login: the browser side of passkey sign-in. The BFF (bff/passkeys.py)
// builds the WebAuthn options and checks the answers; this converts between its
// JSON (base64url) and the ArrayBuffers navigator.credentials works in.

export const toBytes = (b64u) => {
  const b64 = b64u.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (b64u.length % 4)) % 4);
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
};

export const toB64u = (buf) => {
  let bin = '';
  new Uint8Array(buf).forEach((b) => { bin += String.fromCharCode(b); });
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export const passkeysSupported = () =>
  typeof window !== 'undefined' && !!window.PublicKeyCredential
  && typeof navigator !== 'undefined' && !!navigator.credentials;

// The server switch (AUTH_PASSKEYS) AND browser support.
export async function passkeysAvailable() {
  if (!passkeysSupported()) return false;
  try {
    const r = await fetch('/api/me', { credentials: 'include' });
    if (!r.ok) return false;
    return (await r.json()).passkeys === true;
  } catch {
    return false;
  }
}

export async function conditionalMediationAvailable() {
  try {
    return !!(window.PublicKeyCredential.isConditionalMediationAvailable
      && await window.PublicKeyCredential.isConditionalMediationAvailable());
  } catch {
    return false;
  }
}

async function call(method, path, body) {
  const r = await fetch(path, {
    method, credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) {
    const text = await r.text().catch(() => '');
    throw new Error(text || `${r.status} ${r.statusText}`);
  }
  return r.status === 204 ? null : r.json();
}

const descriptors = (list) => (list || []).map((c) => ({ ...c, id: toBytes(c.id) }));
const extensions = (cred) => (cred.getClientExtensionResults ? cred.getClientExtensionResults() : {});

// Resolves to {account_id, profile} once signed in, or null if the browser had no
// passkey to offer. `conditional` waits on the email field's autofill instead of a dialog.
export async function signInWithPasskey({ signal, conditional = false } = {}) {
  const { challenge_id: challengeId, options } = await call('POST', '/auth/passkey/login/options');
  const publicKey = { ...options, challenge: toBytes(options.challenge),
                      allowCredentials: descriptors(options.allowCredentials) };
  const cred = await navigator.credentials.get({
    publicKey, signal, ...(conditional ? { mediation: 'conditional' } : {}),
  });
  if (!cred) return null;
  const r = cred.response;
  return call('POST', '/auth/passkey/login/verify', {
    challenge_id: challengeId,
    credential: {
      id: cred.id, rawId: toB64u(cred.rawId), type: cred.type,
      response: {
        clientDataJSON: toB64u(r.clientDataJSON),
        authenticatorData: toB64u(r.authenticatorData),
        signature: toB64u(r.signature),
        userHandle: r.userHandle ? toB64u(r.userHandle) : null,
      },
      clientExtensionResults: extensions(cred),
    },
  });
}

// A name the person will recognise in their list, from the device they added it on.
export function defaultPasskeyName(ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '')) {
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Macintosh|Mac OS X/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows';
  if (/CrOS/.test(ua)) return 'Chromebook';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Passkey';
}

// Adding a passkey needs a sign-in in the last 10 minutes; past that, pass the
// current password. isReauthRequired(e) tells the caller to ask for it.
export async function addPasskey(name = defaultPasskeyName(), { currentPassword } = {}) {
  const { challenge_id: challengeId, options } = await call(
    'POST', '/auth/passkey/register/options',
    currentPassword ? { current_password: currentPassword } : undefined);
  const publicKey = {
    ...options,
    challenge: toBytes(options.challenge),
    user: { ...options.user, id: toBytes(options.user.id) },
    excludeCredentials: descriptors(options.excludeCredentials),
  };
  const cred = await navigator.credentials.create({ publicKey });
  const r = cred.response;
  return call('POST', '/auth/passkey/register/verify', {
    challenge_id: challengeId,
    name,
    credential: {
      id: cred.id, rawId: toB64u(cred.rawId), type: cred.type,
      response: {
        clientDataJSON: toB64u(r.clientDataJSON),
        attestationObject: toB64u(r.attestationObject),
        transports: r.getTransports ? r.getTransports() : [],
      },
      clientExtensionResults: extensions(cred),
    },
  });
}

export const listPasskeys = async () => (await call('GET', '/auth/passkeys')).passkeys;
// {passkeys, passkey_only}
export const getPasskeyState = () => call('GET', '/auth/passkeys');
// passkey-only-accounts: turn password sign-in off (true) or back on (false).
export const setPasskeyOnly = (on) => call('POST', '/auth/passkey-only', { enabled: on });
export const deleteAccountWithPasskey = () => call('POST', '/auth/delete', {});
export const renamePasskey = (id, name) => call('PATCH', `/auth/passkeys/${id}`, { name });
export const removePasskey = (id) => call('DELETE', `/auth/passkeys/${id}`);

// The person cancelled the browser's passkey dialog (or it timed out): not an error to show.
export const isReauthRequired = (e) => !!(e && /reauth_required/.test(e.message || ''));
// The account signs in with passkeys only, so the re-check is a passkey sign-in, not a password.
export const isPasskeyReauth = (e) => !!(e && /reauth_passkey/.test(e.message || ''));
// A password sign-in refused because the owner turned password sign-in off.
export const isPasskeyOnlyRefusal = (text) => /passkey_only(?!_)/.test(text || '');

// Run fn; if the server wants a fresh passkey sign-in first, do one (it counts as recent)
// and run fn once more.
export async function withPasskeyReauth(fn) {
  try {
    return await fn();
  } catch (e) {
    if (!isPasskeyReauth(e)) throw e;
    if (!(await signInWithPasskey())) throw e;
    return fn();
  }
}

export const isCancel = (e) => e && (e.name === 'NotAllowedError' || e.name === 'AbortError');

// The one-time "add a passkey?" offer after a password sign-in, remembered per
// account in this browser.
const offerKey = (accountId) => `aveq.passkeyOffer.${accountId}`;
export function passkeyOfferDismissed(accountId) {
  try { return window.localStorage.getItem(offerKey(accountId)) === 'done'; } catch { return false; }
}
export function dismissPasskeyOffer(accountId) {
  try { window.localStorage.setItem(offerKey(accountId), 'done'); } catch { /* storage unavailable */ }
}
